import { requireDefined } from "../assertions";

export const getApplicationId = (): string => {

    const content: HTMLElement | null = document.querySelector("body > div.content")
    ?? document.querySelector("body > div[data-appid]");
    return content?.dataset?.["appid"] || "";
};

export const isInStudio = (): boolean => {
    if (window.__SOFTR_E2E_ONLY_APP_MODE__ === "studio") {
        return true;
    }

    return (
        window.location.href.startsWith("http://localhost:4200") ||
        window.location.href.startsWith("https://studio-staging") ||
        window.location.href.startsWith("https://studio.softr.io/")
    );
};

declare global {
    interface Window {
        __SOFTR_E2E_ONLY_APP_MODE__?: string;
    }
}

/**
 * Returns true if the current page is in preview mode.
 *
 * For the local env, we can emulate live mode by removing the `domain` query param from the URL.
 * */
export const isInPreview = (): boolean => {
    const { host, search } = window.location;

    if (host === "localhost:8082") {
        return new URLSearchParams(search).has("domain");
    }
    return host.endsWith(".softr.app") && host.includes(".preview.");
};

export const isLivePage = (): boolean => !isInStudio() && !isInPreview();

export const isPWA = (): boolean =>
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

export interface PageMeta {
    appId: string;
    pageId: string;
}

/**
 * Gets metadata about the current app and page.
 *
 * **NOTE**: This function currently performs a DOM query to get the app and page IDs.
 */
export function getPageMeta(): PageMeta {
    if (process.env.NODE_ENV === "test") {
        return {
            appId: "test-app-id",
            pageId: "test-page-id",
        };
    }

    // Page and App IDs are store in page container div
    // a more sound approach would be to set these values on the window object
    // @MartynasSoftr will open a PR in platform for this so we can migrate.
    const element = document.body.querySelector("[data-appid]") as HTMLElement | null;
    if (!element) throw new Error("Could not find element with data-appid attribute");
    const appId = requireDefined(element.dataset.appid, "Expected data-appid attribute to be defined");
    const pageId = requireDefined(element.dataset.pageid, "Expected data-pageid attribute to be defined");
    return {
        appId,
        pageId,
    };
}

export const isInIframe = () => typeof window !== "undefined" && window !== window.top;
